import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { quoteFormSchema, type QuoteFormData } from '../../types/quote.types';
import Input from '@components/ui/Input';
import Button from '@components/ui/Button';

interface QuoteFormProps {
  onSubmit: (data: QuoteFormData) => void;
  onCancel: () => void;
}

const QuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<QuoteFormData>({
    resolver: zodResolver(quoteFormSchema) as any,
    defaultValues: {
      quantity: 1,
    },
  });

  const handleFormSubmit = (data: QuoteFormData) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Contact Information Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            required
            {...register('firstName')}
            error={errors.firstName?.message}
          />
          <Input
            label="Last Name"
            required
            {...register('lastName')}
            error={errors.lastName?.message}
          />
          <Input
            label="Email"
            type="email"
            required
            {...register('email')}
            error={errors.email?.message}
          />
          <Input
            label="Phone"
            type="tel"
            required
            {...register('phone')}
            error={errors.phone?.message}
          />
        </div>
      </div>

      {/* Rental Preferences Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rental Details</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rental Start Date *
            </label>
            <input
              type="date"
              {...register('rentalDate')}
              className={`w-full px-3 py-2 border ${
                errors.rentalDate ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`}
            />
            {errors.rentalDate && (
              <p className="mt-1 text-sm text-red-600">{errors.rentalDate.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Return Date *</label>
            <input
              type="date"
              {...register('returnDate')}
              className={`w-full px-3 py-2 border ${
                errors.returnDate ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`}
            />
            {errors.returnDate && (
              <p className="mt-1 text-sm text-red-600">{errors.returnDate.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cart Size *</label>
            <select
              {...register('cartSize')}
              className={`w-full px-3 py-2 border ${
                errors.cartSize ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`}
            >
              <option value="">Select Size</option>
              <option value="2">2 Seater</option>
              <option value="4">4 Seater</option>
              <option value="6">6 Seater</option>
              <option value="unsure">Not Sure</option>
            </select>
            {errors.cartSize && (
              <p className="mt-1 text-sm text-red-600">{errors.cartSize.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cart Type</label>
            <select
              {...register('cartType')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option value="">No Preference</option>
              <option value="electric">Electric</option>
              <option value="gas">Gas</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Carts
            </label>
            <input
              type="number"
              min="1"
              {...register('quantity', { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
        </div>
      </div>

      {/* Delivery Location Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Location</h3>
        <p className="text-sm text-gray-600 mb-4">
          We deliver to your location. Please provide the address where you'd like the golf cart(s)
          delivered.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input
              label="Street Address"
              {...register('streetAddress')}
              placeholder="123 Main Street or Hotel/Resort Name"
            />
          </div>
          <Input label="City" {...register('city')} placeholder="Clearwater" />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <select
              {...register('state')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option value="FL">Florida</option>
              <option value="">Other</option>
            </select>
          </div>
          <Input label="ZIP Code" {...register('zipCode')} placeholder="33767" />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Delivery Time Preference
            </label>
            <select
              {...register('deliveryTime')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option value="">No Preference</option>
              <option value="morning">Morning (8AM-12PM)</option>
              <option value="afternoon">Afternoon (12PM-5PM)</option>
              <option value="evening">Evening (5PM-7PM)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Additional Information Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Special Requests or Comments
          </label>
          <textarea
            {...register('comments')}
            rows={4}
            placeholder="Tell us about your specific needs, event details, or any questions you have..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
        </div>
        <div className="mt-4 space-y-2">
          <label className="flex items-center">
            <input type="checkbox" {...register('childSeats')} className="mr-2" />
            <span className="text-sm text-gray-700">Need child seats</span>
          </label>
          <label className="flex items-center">
            <input type="checkbox" {...register('accessories')} className="mr-2" />
            <span className="text-sm text-gray-700">
              Interested in accessories (coolers, phone holders, etc.)
            </span>
          </label>
          <label className="flex items-center">
            <input type="checkbox" {...register('eventRental')} className="mr-2" />
            <span className="text-sm text-gray-700">This is for a special event</span>
          </label>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex gap-4 pt-4">
        <Button type="submit" variant="primary" size="lg" fullWidth>
          Submit Quote Request
        </Button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default QuoteForm;